import { supabaseClient } from './supabase';
import { fetchGHGInventoryData } from '../components/charts/GHGInventorySupabase';
import { supabaseLocationService } from './supabaseLocationService';
import { getBuildingsEnergyData } from './buildingsEnergyDataService';

export interface SectorComparison {
  sector: string;
  year: number;
  precalculated: number;
  liveCalculated: number;
  difference: number;
  percentDifference: number;
}

export interface SectorComparisonResult {
  location: string;
  sectorComparisons: SectorComparison[];
  summary: {
    sector: string;
    totalPrecalculated: number;
    totalLiveCalculated: number;
    totalDifference: number;
    averagePercentDifference: number;
    yearsWithData: number;
  }[];
}

/**
 * Map live calculation dataset labels to pre-calculated table column names
 */
const SECTOR_MAPPING: { [key: string]: string } = {
  'Light Duty Vehicles': 'ldv_emissions',
  'Heavy Duty Vehicles': 'hdv_emissions',
  'Natural Gas Residential': 'ng_residential_emissions',
  'Natural Gas Commercial': 'ng_commercial_emissions',
  'Electricity Residential': 'elec_residential_emissions',
  'Electricity Commercial': 'elec_commercial_emissions',
  'Waste': 'waste_emissions',
  'Offroad': 'offroad_emissions'
};

/**
 * Compare pre-calculated vs live-calculated GHG inventory data at the sector level
 */
export async function compareSectorLevelData(location: string): Promise<SectorComparisonResult> {
  try {
    console.log('🔍 Starting sector-level comparison for:', location);

    // Initialize location service
    await supabaseLocationService.initialize();
    
    // Get geofips for the location
    const geofips = await supabaseLocationService.getGeofips(location);
    if (!geofips) {
      throw new Error(`Could not find geofips for location: ${location}`);
    }

    // Get location info to determine geography type
    const { data: locationData, error: locationError } = await supabaseClient
      .from('population')
      .select('location_name, geography_type, county')
      .eq('geofips', geofips)
      .limit(1);
    
    if (locationError || !locationData || locationData.length === 0) {
      throw new Error(`Could not find location data for: ${location}`);
    }

    // Fetch pre-calculated sector data
    const { data: precalcData, error: precalcError } = await supabaseClient
      .from('ghg_inventory_precalculated')
      .select('*')
      .eq('geofips', geofips)
      .gte('year', 2005)
      .lte('year', 2023)
      .order('year', { ascending: true });

    if (precalcError) {
      throw new Error(`Error fetching pre-calculated data: ${precalcError.message}`);
    }

    if (!precalcData || precalcData.length === 0) {
      throw new Error(`No pre-calculated data found for ${location}`);
    }

    // Fetch live-calculated data
    const liveResult = await fetchGHGInventoryData(location);
    if (!liveResult.chartData || !liveResult.chartData.datasets) {
      throw new Error('No live-calculated chart data available');
    }

    // Build sector comparisons
    const sectorComparisons: SectorComparison[] = [];
    const years = liveResult.chartData.labels as number[];

    // Process each sector
    Object.entries(SECTOR_MAPPING).forEach(([liveLabel, precalcColumn]) => {
      // Find the dataset in live calculation
      const liveDataset = liveResult.chartData.datasets.find((ds: any) => ds.label === liveLabel);
      if (!liveDataset) {
        console.warn(`⚠️ Live dataset not found for sector: ${liveLabel}`);
        return;
      }

      // Compare each year
      years.forEach((year, index) => {
        const liveValue = liveDataset.data[index] || 0;
        
        // Find pre-calculated value for this year
        const precalcRow = precalcData.find((row: any) => row.year === year);
        const precalcValue = precalcRow ? (Number(precalcRow[precalcColumn]) || 0) : 0;

        const difference = liveValue - precalcValue;
        const percentDifference = precalcValue > 0 
          ? (difference / precalcValue) * 100 
          : (liveValue > 0 ? 100 : 0);

        sectorComparisons.push({
          sector: liveLabel,
          year,
          precalculated: precalcValue,
          liveCalculated: liveValue,
          difference,
          percentDifference
        });
      });
    });

    // Calculate summary by sector
    const sectorSummaries = new Map<string, {
      totalPrecalculated: number;
      totalLiveCalculated: number;
      totalDifference: number;
      percentDifferences: number[];
      yearsWithData: number;
    }>();

    sectorComparisons.forEach(comp => {
      if (!sectorSummaries.has(comp.sector)) {
        sectorSummaries.set(comp.sector, {
          totalPrecalculated: 0,
          totalLiveCalculated: 0,
          totalDifference: 0,
          percentDifferences: [],
          yearsWithData: 0
        });
      }

      const summary = sectorSummaries.get(comp.sector)!;
      summary.totalPrecalculated += comp.precalculated;
      summary.totalLiveCalculated += comp.liveCalculated;
      summary.totalDifference += comp.difference;
      
      if (comp.precalculated > 0 || comp.liveCalculated > 0) {
        summary.yearsWithData++;
        if (comp.precalculated > 0) {
          summary.percentDifferences.push(comp.percentDifference);
        }
      }
    });

    const summary = Array.from(sectorSummaries.entries()).map(([sector, data]) => ({
      sector,
      totalPrecalculated: data.totalPrecalculated,
      totalLiveCalculated: data.totalLiveCalculated,
      totalDifference: data.totalDifference,
      averagePercentDifference: data.percentDifferences.length > 0
        ? data.percentDifferences.reduce((a, b) => a + b, 0) / data.percentDifferences.length
        : 0,
      yearsWithData: data.yearsWithData
    }));

    // Log detailed comparison to console
    console.log(`\n${'='.repeat(80)}`);
    console.log(`SECTOR-LEVEL COMPARISON: ${location}`);
    console.log('='.repeat(80));
    
    // For Natural Gas Residential, also show therms comparison
    if (location && precalcData) {
      try {
        const buildingsData = await getBuildingsEnergyData(location);
        const ngResSector = sectorComparisons.filter(c => c.sector === 'Natural Gas Residential');
        
        if (ngResSector.length > 0) {
          console.log(`\n📊 Natural Gas Residential (with therms breakdown):`);
          console.log('Year | Pre-calc Therms | Live Therms | Therms Diff | Pre-calc Emissions | Live Emissions | Emissions Diff | % Diff');
          console.log('-'.repeat(120));
          
          ngResSector.forEach(comp => {
            const precalcRow = precalcData.find((r: any) => r.year === comp.year);
            const precalcTherms = precalcRow?.ng_residential_therms ? Number(precalcRow.ng_residential_therms) : null;
            
            const yearIndex = buildingsData.naturalGas.residential.years.indexOf(comp.year);
            const liveTherms = yearIndex >= 0 ? (buildingsData.naturalGas.residential.values[yearIndex] || null) : null;
            
            const thermsDiff = precalcTherms && liveTherms ? liveTherms - precalcTherms : null;
            const thermsPctDiff = precalcTherms && thermsDiff !== null ? (thermsDiff / precalcTherms) * 100 : null;
            
            const matchStatus = Math.abs(comp.percentDifference) < 0.1 ? '✅' : '⚠️';
            
            console.log(
              `${comp.year} | ${precalcTherms?.toLocaleString().padStart(16) || 'N/A'.padStart(16)} | ${liveTherms?.toLocaleString().padStart(12) || 'N/A'.padStart(12)} | ${thermsDiff !== null ? (thermsDiff >= 0 ? '+' : '') + thermsDiff.toLocaleString().padStart(11) : 'N/A'.padStart(11)} | ${comp.precalculated.toLocaleString().padStart(19)} | ${comp.liveCalculated.toLocaleString().padStart(15)} | ${comp.difference >= 0 ? '+' : ''}${comp.difference.toLocaleString().padStart(13)} | ${comp.percentDifference >= 0 ? '+' : ''}${comp.percentDifference.toFixed(2)}% ${matchStatus}`
            );
            
            if (thermsPctDiff !== null && Math.abs(thermsPctDiff) > 0.1) {
              console.log(`     → Therms difference explains ${thermsPctDiff.toFixed(2)}% of emissions difference`);
            }
          });
        }
      } catch (error) {
        console.warn('⚠️ Could not fetch buildings data for therms comparison:', error);
      }
    }

    Object.keys(SECTOR_MAPPING).forEach(sector => {
      const sectorData = sectorComparisons.filter(c => c.sector === sector);
      if (sectorData.length === 0) return;

      // Skip Natural Gas Residential if we already showed it with therms
      if (sector === 'Natural Gas Residential') return;

      console.log(`\n📊 ${sector}:`);
      console.log('Year | Pre-calculated | Live-calculated | Difference | % Difference');
      console.log('-'.repeat(70));
      
      sectorData.forEach(comp => {
        if (comp.precalculated > 0 || comp.liveCalculated > 0) {
          const matchStatus = Math.abs(comp.percentDifference) < 0.1 ? '✅' : '⚠️';
          console.log(
            `${comp.year} | ${comp.precalculated.toLocaleString().padStart(14)} | ${comp.liveCalculated.toLocaleString().padStart(15)} | ${comp.difference >= 0 ? '+' : ''}${comp.difference.toLocaleString().padStart(10)} | ${comp.percentDifference >= 0 ? '+' : ''}${comp.percentDifference.toFixed(2)}% ${matchStatus}`
          );
        }
      });

      const sectorSummary = summary.find(s => s.sector === sector);
      if (sectorSummary) {
        console.log(`\n  Total Pre-calculated: ${sectorSummary.totalPrecalculated.toLocaleString()} MT CO₂e`);
        console.log(`  Total Live-calculated: ${sectorSummary.totalLiveCalculated.toLocaleString()} MT CO₂e`);
        console.log(`  Total Difference: ${sectorSummary.totalDifference >= 0 ? '+' : ''}${sectorSummary.totalDifference.toLocaleString()} MT CO₂e`);
        console.log(`  Average % Difference: ${sectorSummary.averagePercentDifference >= 0 ? '+' : ''}${sectorSummary.averagePercentDifference.toFixed(2)}%`);
      }
    });

    console.log(`\n${'='.repeat(80)}\n`);

    return {
      location,
      sectorComparisons,
      summary
    };

  } catch (error) {
    console.error('❌ Error comparing sector-level data:', error);
    throw error;
  }
}

/**
 * Generate CSV data from sector comparison results
 */
export function generateSectorComparisonCSV(result: SectorComparisonResult): any[] {
  const csvData: any[] = [];
  
  // Add header row
  // We'll add data rows for each sector and year combination
  
  // Group by sector
  const sectors = new Set(result.sectorComparisons.map(c => c.sector));
  
  sectors.forEach(sector => {
    const sectorData = result.sectorComparisons.filter(c => c.sector === sector);
    
    // Add rows for each year in this sector
    sectorData.forEach(comp => {
      csvData.push({
        Sector: comp.sector,
        Year: comp.year,
        'Pre-calculated (MT CO2e)': comp.precalculated.toFixed(2),
        'Live-calculated (MT CO2e)': comp.liveCalculated.toFixed(2),
        'Difference (MT CO2e)': comp.difference.toFixed(2),
        'Percent Difference (%)': comp.percentDifference.toFixed(2)
      });
    });
    
    // Add summary row for this sector
    const sectorSummary = result.summary.find(s => s.sector === sector);
    if (sectorSummary) {
      csvData.push({
        Sector: `${sector} (Summary)`,
        Year: 'All Years',
        'Pre-calculated (MT CO2e)': sectorSummary.totalPrecalculated.toFixed(2),
        'Live-calculated (MT CO2e)': sectorSummary.totalLiveCalculated.toFixed(2),
        'Difference (MT CO2e)': sectorSummary.totalDifference.toFixed(2),
        'Percent Difference (%)': sectorSummary.averagePercentDifference.toFixed(2)
      });
    }
    
    // Add empty row between sectors for readability
    csvData.push({
      Sector: '',
      Year: '',
      'Pre-calculated (MT CO2e)': '',
      'Live-calculated (MT CO2e)': '',
      'Difference (MT CO2e)': '',
      'Percent Difference (%)': ''
    });
  });
  
  return csvData;
}

